import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged, signOut, signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { ref, get } from 'firebase/database';
import { getAuthInstance, getDatabaseInstance } from '../services/recruiterApi';
import { detectUserCompany } from '../utils/companyUtils';
// Re-export forceRefreshClaim so callers can import from either authService or AuthContext.
export { forceRefreshClaim } from '../services/authService';

const AuthContext = createContext(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let unsubscribe;
    let mounted = true;
    let lastProcessedUid = null;
    
    try {
      const auth = getAuthInstance();
      const database = getDatabaseInstance();
      
      unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        console.log('Auth state changed:', firebaseUser ? 'User logged in' : 'User logged out');
        
        // Prevent processing the same user multiple times
        if (firebaseUser && firebaseUser.uid === lastProcessedUid) {
          console.log('Skipping duplicate auth state change for user:', firebaseUser.uid);
          return;
        }
        
        try {
          if (firebaseUser && mounted) {
            lastProcessedUid = firebaseUser.uid;
            console.log('Fetching user data for:', firebaseUser.uid);
            
            // Use company-aware user detection
            const companyInfo = await detectUserCompany(firebaseUser.uid);
            
            if (companyInfo && companyInfo.userData) {
              const fullUserData = {
                ...firebaseUser,
                ...companyInfo.userData,
                uid: firebaseUser.uid,
                email: firebaseUser.email,
                companyId: companyInfo.companyId,
                companyName: companyInfo.companyName,
                isLegacy: companyInfo.isLegacy
              };
              
              console.log('User data fetched successfully:', { 
                uid: fullUserData.uid, 
                email: fullUserData.email, 
                role: fullUserData.role,
                companyId: companyInfo.companyId,
                companyName: companyInfo.companyName,
                isLegacy: companyInfo.isLegacy
              });
              
              if (mounted) {
                setUser(fullUserData);
                setError(null);
              }
            } else {
              console.log('No user data found, user may need company assignment');
              if (mounted) {
                setUser({
                  ...firebaseUser,
                  uid: firebaseUser.uid,
                  email: firebaseUser.email,
                  role: 'user', // fallback role
                  needsCompanyAssignment: true
                });
                setError(null);
              }
            }
          } else if (mounted) {
            lastProcessedUid = null;
            setUser(null);
            setError(null);
          }
        } catch (err) {
          console.error('Error fetching user data:', err);
          if (mounted) {
            setError(err.message);
            // Still set the firebase user even if we can't get additional data
            if (firebaseUser) {
              setUser({
                ...firebaseUser,
                uid: firebaseUser.uid,
                email: firebaseUser.email,
                role: 'user' // fallback role
              });
            }
          }
        } finally {
          if (mounted) {
            setLoading(false);
          }
        }
        });
    } catch (err) {
      console.error('Error setting up auth state listener:', err);
      if (mounted) {
        setError(err.message);
        setLoading(false);
      }
    }

    return () => {
      mounted = false;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const logout = async () => {
    try {
      console.log('Logging out user');
      const auth = getAuthInstance();
      await signOut(auth);
      setUser(null);
      setError(null);
      console.log('Logout successful');
    } catch (error) {
      console.error('Error signing out:', error);
      setError(error.message);
      throw error;
    }
  };

  const signIn = async (email, password) => {
    try {
      console.log('Attempting to sign in with email:', email);
      setError(null);
      const auth = getAuthInstance();
      const result = await signInWithEmailAndPassword(auth, email, password);
      console.log('Firebase sign in successful:', result.user.uid);

      // The onAuthStateChanged listener will handle setting the user state
      // We return the result so the component can handle any immediate needs
      return result;
    } catch (error) {
      console.error('Error signing in:', error);
      setError(error.message);
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    try {
      console.log('Attempting to sign in with Google');
      setError(null);
      const auth = getAuthInstance();
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      console.log('Google sign in successful:', result.user.uid);

      // The onAuthStateChanged listener will handle setting the user state
      return result;
    } catch (error) {
      console.error('Error signing in with Google:', error);
      setError(error.message);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    error,
    logout,
    signIn,
    signInWithGoogle
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 